import { AfterViewInit, Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface ComposerSubmitPayload {
  text: string;
  file?: File;
}

@Component({
  standalone: true,
  selector: 'app-message-composer',
  imports: [CommonModule, FormsModule],
  template: `
  <form class="composer" (submit)="submit($event)">
    <input
      #fileInput
      class="fileInput"
      type="file"
      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
      (change)="onFileChange($event)" />
    <button type="button" class="attach" (click)="openFilePicker()" aria-label="Attach file">📎</button>
    <textarea
      #draftInput
      [(ngModel)]="draft"
      name="draft"
      autocomplete="off"
      placeholder="Type a message"
      aria-label="Message input"
      rows="1"
      (input)="resizeComposer()"
      (keydown)="onComposerKeydown($event)"></textarea>
    <button class="send" [disabled]="!canSend()" aria-label="Send">➤</button>
  </form>
  <div class="fileRow" *ngIf="selectedFileName">
    <span class="fileTag">{{ selectedFileName }}</span>
    <button type="button" class="removeFile" (click)="clearSelectedFile()" aria-label="Remove file">✕</button>
  </div>
  `,
  styles: [`
    :host{display:block}
    .composer{
      display:flex;
      gap:10px;
      align-items:flex-end;
      padding:10px 12px;
      border-top:1px solid var(--border);
      background:var(--panel);
    }
    .fileInput{display:none}
    .attach{
      width:44px;height:44px;border-radius:14px;border:1px solid var(--border);
      background:var(--input);color:var(--text);cursor:pointer;font-size:18px;line-height:1
    }
    .attach:hover{border-color:rgba(0,168,132,.45)}
    textarea{
      flex:1;
      border:1px solid var(--border);
      background:var(--input);
      color:var(--text);
      padding:11px 12px;
      border-radius:14px;
      outline:none;
      font-size:14px;
      min-width:0;
      min-height:44px;
      max-height:160px;
      resize:none;
      overflow-y:auto;
      line-height:1.4;
      font-family:inherit;
    }
    .send{width:44px;height:44px;border-radius:14px;border:1px solid rgba(0,168,132,.45);background:rgba(0,168,132,.20);color:var(--text);cursor:pointer;font-weight:900}
    .send:disabled{opacity:.5;cursor:not-allowed}
    .fileRow{
      display:flex;
      align-items:center;
      gap:8px;
      padding:0 12px 10px;
      background:var(--panel);
    }
    .fileTag{
      display:inline-flex;
      align-items:center;
      max-width:min(560px, calc(100vw - 160px));
      overflow:hidden;
      text-overflow:ellipsis;
      white-space:nowrap;
      border:1px solid var(--border);
      border-radius:999px;
      padding:4px 10px;
      font-size:12px;
      color:var(--muted);
    }
    .removeFile{
      border:0;
      background:transparent;
      color:var(--muted);
      cursor:pointer;
      font-size:14px;
      line-height:1;
      padding:2px 4px;
    }
    .removeFile:hover{color:var(--text)}
  `]
})
export class MessageComposerComponent implements AfterViewInit {
  @Output() send = new EventEmitter<ComposerSubmitPayload>();
  @ViewChild('draftInput') draftInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  draft = '';
  selectedFile?: File;
  selectedFileName = '';

  ngAfterViewInit(): void {
    this.resizeComposer();
  }

  onComposerKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submit(e);
    }
  }

  openFilePicker(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const file = input.files?.item(0) || undefined;
    this.selectedFile = file;
    this.selectedFileName = file?.name || '';
  }

  clearSelectedFile(): void {
    this.selectedFile = undefined;
    this.selectedFileName = '';
    const input = this.fileInput?.nativeElement;
    if (input) input.value = '';
  }

  resizeComposer(): void {
    const el = this.draftInput?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  canSend(): boolean {
    return !!this.draft.trim() || !!this.selectedFile;
  }

  submit(e: Event) {
    e.preventDefault();
    const t = this.draft.trim();
    if (!t && !this.selectedFile) return;
    this.send.emit({ text: t, file: this.selectedFile });
    this.draft = '';
    this.clearSelectedFile();
    const el = this.draftInput?.nativeElement;
    if (el) {
      el.value = '';
      el.style.height = '44px';
    }
    queueMicrotask(() => this.resizeComposer());
  }
}
